import React, { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'hooks';
import ItemConfiguration from 'types/ItemConfiguration';
import Checkbox from 'components/ui/checkbox';
import Icon from 'components/ui/icon';
import Utils from 'helpers/Utils';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';

import './configuration.scss';

interface Props {
  configuration: ItemConfiguration[];
  changeConfiguration: (configuration: ItemConfiguration[]) => void;
}

export const Configuration: FC<Props> = ({
  configuration,
  changeConfiguration,
}) => {
  const getTranslate = useTranslation();
  const fixedItems = configuration.filter((item) =>
    Utils.hasProp(item, 'fixed')
  );
  const sortableItems = configuration.filter(
    (item) => !Utils.hasProp(item, 'fixed')
  );

  // For multiple fixed field activation if one of the lock clicked
  const handleChangeItemFixed = () => {
    const newConfig = configuration.map(({ fixed, ...rest }) => {
      if (fixed !== undefined) {
        return {
          ...rest,
          fixed: !fixed,
        };
      }

      return rest;
    });

    changeConfiguration(newConfig);
  };

  const handleChangeItemActive = (id: string) => {
    const newConfig = configuration.map((item) => {
      if (item.id === id) {
        return { ...item, active: !item.active };
      }

      return item;
    });

    changeConfiguration(newConfig);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = configuration.findIndex((item) => item.id === active.id);
    const newIndex = configuration.findIndex((item) => item.id === over.id);
    changeConfiguration(arrayMove(configuration, oldIndex, newIndex));
  };

  return (
    <div className='configuration__content'>
      <div className='configuration__list'>
        {fixedItems.map(({ id, fixed, localId }, index) => {
          return (
            <div className='configuration__item' key={id}>
              <span className='configuration__item-position'>{index + 1}</span>
              <label
                className='ui-checkbox ui-checkbox_locker'
                onClick={handleChangeItemFixed}>
                <span className='ui-checkbox__icon'>
                  <Icon
                    name={fixed ? 'im-Lock-close' : 'im-Lock-open'}
                    size={18}
                  />
                </span>
                <span className='ui-checkbox__text'>
                  {getTranslate(localId)}
                </span>
              </label>
            </div>
          );
        })}

        <div>
          <DndContext
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}>
            <SortableContext
              items={configuration}
              strategy={verticalListSortingStrategy}>
              {sortableItems.map(({ id, active, localId }, index) => {
                return (
                  <SortableItem
                    key={id}
                    id={id}
                    index={index + fixedItems.length}
                    active={active}
                    localId={localId}
                    onSelect={handleChangeItemActive}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

const SortableItem: FC<{
  id: string;
  index: number;
  active: boolean;
  localId: string;
  onSelect: (id: string) => void;
}> = ({ id, index, active, localId, onSelect }) => {
  const getTranslate = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !active });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      key={id}
      ref={setNodeRef}
      className={classNames('configuration__item', {
        configuration__item_dragging: isDragging,
      })}
      style={style}
      {...attributes}>
      <span className='configuration__item-position'>{index + 1}</span>
      <div
        className={classNames('configuration__drag-handler', {
          'configuration__drag-handler_dragging': isDragging,
          'configuration__drag-handler_disabled': !active,
        })}
        {...listeners}>
        <Icon
          className='configuration__drag-handler-icon'
          name='im-Draganddrop'
          size={12}
        />
      </div>
      <Checkbox
        id={id}
        text={getTranslate(localId)}
        checked={active}
        onChange={() => onSelect(id)}
      />
    </div>
  );
};
